import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { MessageSquare, Clock, AlertCircle, CheckCircle, User, MessageCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { FeedbackThread } from './FeedbackThread';

interface ClientFeedback {
  id: string;
  event_id: string;
  client_id: string;
  feedback_type: string;
  message: string;
  priority: string;
  status: string;
  response_message: string | null;
  responded_at: string | null;
  created_at: string;
  event_name?: string;
  client_name?: string;
  client_email?: string;
}

interface ClientFeedbackManagerProps {
  eventId?: string;
}

export const ClientFeedbackManager = ({ eventId }: ClientFeedbackManagerProps) => {
  const [feedback, setFeedback] = useState<ClientFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<ClientFeedback | null>(null);
  const hasLoadedRef = useRef(false);
  
  const { user } = useAuth();

  useEffect(() => {
    // Only load once when user is available and hasn't loaded yet
    if (user?.id && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadFeedback();
    }
  }, [user?.id, eventId]);

  // Real-time subscription for feedback updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('client-feedback-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_feedback'
        },
        () => {
          loadFeedback();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, eventId]);

  const loadFeedback = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // First, get the user's events
      let eventsQuery = supabase
        .from('events')
        .select('id, name')
        .eq('user_id', user.id);
      
      if (eventId) {
        eventsQuery = eventsQuery.eq('id', eventId);
      }
      
      const { data: userEvents, error: eventsError } = await eventsQuery;
      
      if (eventsError) {
        console.error('Error loading events:', eventsError);
        setLoading(false);
        return;
      }
      
      if (!userEvents || userEvents.length === 0) {
        setFeedback([]);
        setLoading(false);
        return;
      }
      
      const eventIds = userEvents.map(e => e.id);
      const eventNameMap = new Map(userEvents.map(e => [e.id, e.name]));
      
      // Now get feedback for those events
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('client_feedback')
        .select(`
          *,
          clients (name, email)
        `)
        .in('event_id', eventIds)
        .order('created_at', { ascending: false });
      
      if (feedbackError) {
        console.error('Error loading feedback:', feedbackError);
        setLoading(false);
        return;
      }
      
      const transformedFeedback = (feedbackData || []).map((item: any) => ({
        ...item,
        event_name: eventNameMap.get(item.event_id) || 'Unknown Event',
        client_name: item.clients?.name,
        client_email: item.clients?.email,
      }));
      
      setFeedback(transformedFeedback);
    } catch (error) {
      console.error('Error in loadFeedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <AlertCircle className="w-4 h-4 text-blue-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading feedback...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (feedback.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            <CardTitle>Client Feedback</CardTitle>
          </div>
          <CardDescription>
            Manage and respond to client feedback
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No feedback received yet</p>
            <p className="text-sm">Client feedback will appear here when submitted</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          <CardTitle>Client Feedback</CardTitle>
        </div>
        <CardDescription>
          {feedback.length} feedback item{feedback.length !== 1 ? 's' : ''} to review
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {feedback.map((item) => (
          <div 
            key={item.id} 
            className="border rounded-lg p-4 space-y-3 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setSelectedFeedback(item)}
          >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
              <div className="space-y-1 min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusIcon(item.status)}
                  <span className="font-medium truncate">{item.event_name}</span>
                  {item.client_name && (
                    <Badge variant="outline" className="text-xs">
                      <User className="w-3 h-3 mr-1" />
                      {item.client_name}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                  <span className="capitalize">{item.feedback_type.replace('_', ' ')}</span>
                  <span>•</span>
                  <span>{formatDate(item.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge className={getPriorityColor(item.priority)}>
                  {item.priority}
                </Badge>
                <Button variant="ghost" size="sm">
                  <MessageCircle className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">View Thread</span>
                </Button>
              </div>
            </div>
            
            <div className="bg-muted/50 rounded-md p-3">
              <p className="text-sm line-clamp-2">{item.message}</p>
            </div>
          </div>
        ))}
      </CardContent>

      {/* Feedback Thread Dialog */}
      <Dialog open={!!selectedFeedback} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] mx-4 sm:mx-auto">
          {selectedFeedback && (
            <FeedbackThread
              feedbackId={selectedFeedback.id}
              feedbackMessage={selectedFeedback.message}
              feedbackType={selectedFeedback.feedback_type}
              feedbackPriority={selectedFeedback.priority}
              feedbackStatus={selectedFeedback.status}
              eventName={selectedFeedback.event_name || 'Event'}
              userRole="event_planner"
              userName={user?.user_metadata?.display_name || user?.email?.split('@')[0]}
              authorName={selectedFeedback.client_name || 'Client'}
              createdAt={selectedFeedback.created_at}
              canReply={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ClientFeedbackManager;
